import { useState, type FormEvent } from 'react';
import {
  useExpiringItems,
  useCreateExpiringItem,
  useUpdateExpiringItem,
  useDeleteExpiringItem,
} from '../api/queries/expiring-items';
import { useAssignableUsers } from '../api/queries/project-tasks';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { fmtDate } from '../utils/date';
import { DateInput } from '../components/DateInput';
import { LookupSelect } from '../components/LookupSelect';
import { IconAction } from '../components/IconAction';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { useLookupLabel } from '../api/queries/lookups';
import type { ExpiringItemCategory } from '@stfors/shared-types';
import './ExpiringPage.css';
import { Toggle } from '../components/Toggle';

type ExpiringSortKey = 'name' | 'category' | 'owner' | 'issued' | 'expires' | 'status';

function todayEpoch(): number {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}

function daysUntil(epochMs: number): number {
  const ms = epochMs - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export function ExpiringPage(): JSX.Element {
  const [includeExpired, setIncludeExpired] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<ExpiringItemCategory | ''>('');

  const filters = {
    include_expired: includeExpired,
    ...(categoryFilter ? { category: categoryFilter } : {}),
  };
  const { data, isPending, error } = useExpiringItems(filters);
  const usersQuery = useAssignableUsers();
  const createItem = useCreateExpiringItem();
  const updateItem = useUpdateExpiringItem();
  const deleteItem = useDeleteExpiringItem();
  const categoryLabel = useLookupLabel('expiring_item_category');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editExpires, setEditExpires] = useState<number | null>(null);
  const [editReminder, setEditReminder] = useState('30');
  const [editNotes, setEditNotes] = useState('');

  const startItemEdit = (i: { id: number; name: string; expires_at: number; reminder_days_before: number; notes: string | null }) => {
    setEditingId(i.id);
    setEditName(i.name);
    setEditExpires(i.expires_at);
    setEditReminder(String(i.reminder_days_before));
    setEditNotes(i.notes ?? '');
  };

  const saveItemEdit = (id: number) => {
    if (editExpires === null) return;
    updateItem.mutate(
      {
        id,
        name: editName.trim(),
        expires_at: editExpires,
        reminder_days_before: Number.isInteger(Number(editReminder))
          ? Number(editReminder)
          : undefined,
        notes: editNotes.trim() || null,
      },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const [showNew, setShowNew] = useState(false);
  const [newCategory, setNewCategory] = useState<ExpiringItemCategory>('professional_license');
  const [newName, setNewName] = useState('');
  const [newOwner, setNewOwner] = useState<number | ''>('');
  const [newIssued, setNewIssued] = useState<number | null>(null);
  const [newExpires, setNewExpires] = useState<number | null>(() => todayEpoch());
  const [newReminder, setNewReminder] = useState('30');
  const [newNotes, setNewNotes] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newName.trim().length === 0) return;
    if (newExpires === null) {
      setCreateError('Pick an expiration date.');
      return;
    }
    try {
      await createItem.mutateAsync({
        category: newCategory,
        name: newName.trim(),
        owner_user_id: newOwner === '' ? undefined : newOwner,
        issued_at: newIssued ?? undefined,
        expires_at: newExpires,
        reminder_days_before: Number.isInteger(Number(newReminder))
          ? Number(newReminder)
          : undefined,
        notes: newNotes.trim() || undefined,
      });
      setShowNew(false);
      setNewName('');
      setNewIssued(null);
      setNewNotes('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { fieldErrors?: Record<string, string[]> } } | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const first = Object.values(fe).find((arr) => Array.isArray(arr) && arr.length > 0);
          if (first && Array.isArray(first)) {
            setCreateError(first[0]!);
            return;
          }
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setCreateError("You don't have permission to add expiring items.");
        return;
      }
      setCreateError('Could not save. Try again.');
    }
  };

  const items = data?.expiring_items ?? [];
  const users = usersQuery.data?.users ?? [];
  const sort = useClientSort(items, (i, key: ExpiringSortKey) => {
    switch (key) {
      case 'name': return i.name;
      case 'category': return categoryLabel(i.category);
      case 'owner': return i.owner_name ?? '';
      case 'issued': return i.issued_at ?? null;
      case 'expires': return i.expires_at;
      case 'status': return daysUntil(i.expires_at);
      default: return '';
    }
  }, { key: 'expires', dir: 'asc' });

  if (isPending) return <p className="ei-status">Loading expiring items…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view expiring items."
        : 'Failed to load expiring items.';
    return <p className="ei-status ei-error">{msg}</p>;
  }

  const expiringSoon = items.filter((i) => {
    const d = daysUntil(i.expires_at);
    return d >= 0 && d <= i.reminder_days_before;
  }).length;
  const expired = items.filter((i) => daysUntil(i.expires_at) < 0).length;

  return (
    <section className="page-stack page-stack-wide">
      <div className="card ei-toolbar">
      <header className="ei-header">
        <h2>Expiring Items</h2>
        <div className="ei-header-controls">
          <LookupSelect
            category="expiring_item_category"
            value={categoryFilter}
            onChange={setCategoryFilter}
            className="form-input ei-filter"
            allowEmpty
            emptyLabel="All categories"
          />
          <Toggle
            checked={includeExpired}
            onChange={setIncludeExpired}
            label="Include expired"
          />
          {can('expiring_item.write') && !showNew && (
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={() => setShowNew(true)}
            >
              New item
            </button>
          )}
        </div>
      </header>
      </div>

      <div className="card">
        <div className="ei-summary">
          <div className="ei-summary-card">
            <div className="ei-summary-label">Tracked</div>
            <div className="ei-summary-value">{items.length}</div>
          </div>
          <div className="ei-summary-card">
            <div className="ei-summary-label">Expiring soon</div>
            <div className={`ei-summary-value ${expiringSoon > 0 ? 'ei-summary-warn' : ''}`}>
              {expiringSoon}
            </div>
          </div>
          {includeExpired && (
            <div className="ei-summary-card">
              <div className="ei-summary-label">Expired</div>
              <div className={`ei-summary-value ${expired > 0 ? 'ei-summary-danger' : ''}`}>
                {expired}
              </div>
            </div>
          )}
        </div>
      </div>

      {showNew && (
        <div className="card">
        <form className="ei-new-form" onSubmit={onCreate}>
          <div className="ei-new-row">
            <label className="ei-field">
              <span>Category</span>
              <LookupSelect
                category="expiring_item_category"
                value={newCategory}
                onChange={setNewCategory}
                className="form-input"
                disabled={createItem.isPending}
              />
            </label>
            <label className="ei-field ei-field-wide">
              <span>Name *</span>
              <input
                type="text"
                className="form-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. EnCE certification, GA notary, business liability insurance"
                maxLength={200}
                disabled={createItem.isPending}
                required
              />
            </label>
          </div>
          <div className="ei-new-row">
            <label className="ei-field">
              <span>Owner</span>
              <select
                className="form-input"
                value={newOwner}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewOwner(v === '' ? '' : Number(v));
                }}
                disabled={createItem.isPending}
              >
                <option value="">(default to creator)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name}</option>
                ))}
              </select>
            </label>
            <label className="ei-field">
              <span>Issued</span>
              <DateInput
                value={newIssued}
                onChange={setNewIssued}
                disabled={createItem.isPending}
              />
            </label>
            <label className="ei-field">
              <span>Expires *</span>
              <DateInput
                value={newExpires}
                onChange={setNewExpires}
                disabled={createItem.isPending}
                required
              />
            </label>
            <label className="ei-field">
              <span>Reminder (days)</span>
              <input
                type="number"
                className="form-input"
                value={newReminder}
                onChange={(e) => setNewReminder(e.target.value)}
                min={0}
                max={3650}
                disabled={createItem.isPending}
              />
            </label>
          </div>
          <label className="ei-field ei-field-wide">
            <span>Notes</span>
            <textarea
              className="form-input ei-textarea"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              rows={2}
              maxLength={10_000}
              disabled={createItem.isPending}
              placeholder="Optional — policy number, renewal contact, etc."
            />
          </label>
          {createError && <p className="ei-error-banner" role="alert">{createError}</p>}
          <div className="ei-new-actions">
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setShowNew(false);
                setCreateError(null);
              }}
              disabled={createItem.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={createItem.isPending || newName.trim().length === 0}
            >
              {createItem.isPending ? 'Saving…' : 'Save item'}
            </button>
          </div>
        </form>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card">
          <div className="ei-empty">
            <p>Nothing tracked yet.</p>
            {can('expiring_item.write') && (
              <p className="ei-empty-meta">
                Click "New item" to start tracking renewals.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-tight ei-table-card table-scroll-card">
        <TableScroll id="expiring">
        <table className="ei-table">
          <thead>
            <tr>
              <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="category" label="Category" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="owner" label="Owner" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="issued" label="Issued" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="expires" label="Expires" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              {(can('expiring_item.write') || can('expiring_item.delete')) && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((i) => {
              const days = daysUntil(i.expires_at);
              const expired = days < 0;
              const soon = !expired && days <= i.reminder_days_before;
              const statusClass = expired ? 'ei-pill-danger' : soon ? 'ei-pill-warn' : 'ei-pill-ok';
              const statusLabel = expired
                ? `Expired ${Math.abs(days)}d ago`
                : days === 0
                ? 'Expires today'
                : `In ${days}d`;
              const isEditing = editingId === i.id;
              return (
                <tr key={i.id} className={expired ? 'ei-row-expired' : ''}>
                  <td className="ei-cell-name">
                    {isEditing ? (
                      <input
                        type="text"
                        className="form-input ei-edit-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        disabled={updateItem.isPending}
                      />
                    ) : i.name}
                  </td>
                  <td>{categoryLabel(i.category)}</td>
                  <td>{i.owner_name ?? '—'}</td>
                  <td>{fmtDate(i.issued_at)}</td>
                  <td>
                    {isEditing ? (
                      <DateInput
                        className="ei-edit-input"
                        value={editExpires}
                        onChange={setEditExpires}
                        disabled={updateItem.isPending}
                      />
                    ) : fmtDate(i.expires_at)}
                  </td>
                  <td><span className={`ei-pill ${statusClass}`}>{statusLabel}</span></td>
                  <td className="ei-actions-cell">
                    {can('expiring_item.write') && (
                      isEditing ? (
                        <>
                          <IconAction
                            action="save"
                            onClick={() => saveItemEdit(i.id)}
                            disabled={updateItem.isPending}
                            iconSize={16}
                          />
                          <IconAction
                            action="cancel"
                            onClick={() => setEditingId(null)}
                            disabled={updateItem.isPending}
                            iconSize={16}
                          />
                        </>
                      ) : (
                        <IconAction
                          action="edit"
                          onClick={() => startItemEdit(i)}
                          iconSize={16}
                        />
                      )
                    )}
                    {!isEditing && can('expiring_item.delete') && (
                      <IconAction
                        action="delete"
                        onClick={() => {
                          if (confirm(`Delete "${i.name}"?`)) deleteItem.mutate(i.id);
                        }}
                        disabled={deleteItem.isPending}
                        iconSize={16}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}
